import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  OnDestroy,
  signal,
  computed,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import {
  ClockIconComponent,
  DangerIconComponent,
  DirectBoxReactiveIconComponent,
  MailIconComponent,
  MonitorIconComponent,
  UserOctagonIconComponent,
  TickSquareIconComponent,
  MentionIconComponent,
  ResumeIconComponent,
  PendingIconComponent,
  NotificationStatusIconComponent,
  ReactionIconComponent,
  UserTwoIconComponent,
  UserNotificationIconComponent,
  ArrowUpCircleIconComponent,
  MoneyAddIconComponent,
  MoneysIconComponent,
  CardAddIconComponent,
  CardEditIconComponent,
} from '@kuiper/icons';
import { AccountSettingsService } from '../account-settings.service';
import { NotificationSetting } from '../notification-settings.model';
import {
  Subject,
  debounceTime,
  takeUntil,
  startWith,
  pairwise,
  forkJoin,
} from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { SlaNotificationTimeSetting } from '../account-settings.enum';
import {
  NotificationType,
  NotificationTypeValue,
  NotificationChannelValue,
  NotificationChannel,
} from '../../../../../ui-components/lib/notification/notifications-type.enum';
import { IconButtonToggleComponent } from './../../../../../ui-components/lib/icon-button-toggle/icon-button-toggle.component';
import { LoaderComponent } from './../../../../../ui-components/lib/loader/loader.component';
import { SelectComponent } from './../../../../../ui-components/lib/select/select.component';
import { OptionItem } from '../../../../../ui-components/lib/option.interface';
import { NotificationTabInsight } from '../../../../common/models/notification-tab-insight.model';

@Component({
  selector: 'lib-notification-tab',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IconButtonToggleComponent,
    SelectComponent,
    ClockIconComponent,
    DirectBoxReactiveIconComponent,
    DangerIconComponent,
    TranslocoPipe,
    MonitorIconComponent,
    MailIconComponent,
    UserOctagonIconComponent,
    TickSquareIconComponent,
    LoaderComponent,
    MentionIconComponent,
    ResumeIconComponent,
    PendingIconComponent,
    NotificationStatusIconComponent,
    ReactionIconComponent,
    UserTwoIconComponent,
    UserNotificationIconComponent,
    ArrowUpCircleIconComponent,
    MoneyAddIconComponent,
    MoneysIconComponent,
    CardAddIconComponent,
    CardEditIconComponent,
  ],
  templateUrl: './notification-tab.component.html',
  styleUrl: './notification-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NotificationTabComponent implements OnInit, OnDestroy {
  private readonly accountSettingsService = inject(AccountSettingsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  NotificationTypeValue = NotificationTypeValue;
  NotificationChannelValue = NotificationChannelValue;

  insights = signal<NotificationTabInsight>({
    total_notifications_count: 0,
    unread_notifications_count: 0,
  });

  notificationForm!: FormGroup;
  notificationSettings = signal<NotificationSetting[]>([]);
  notificationTypes = signal<NotificationType[]>([]);

  isLoading = signal<boolean>(true);
  isInitialized = signal<boolean>(false);

  private readonly channelLabels: Record<NotificationChannelValue, string> = {
    [NotificationChannelValue.IN_APP]: 'In App',
    [NotificationChannelValue.MAIL]: 'Email',
  };

  readonly allChannels: NotificationChannel[] = Object.values(
    NotificationChannelValue
  ).map(value => ({ value, label: this.channelLabels[value] }));

  readonly missingChannelsMap = computed<Record<string, Set<string>>>(() => {
    const allValues = this.allChannels.map(c => c.value);
    const result: Record<string, Set<string>> = {};
    for (const type of this.notificationTypes()) {
      const present = new Set(type.channels.map(c => c.value));
      result[type.value] = new Set(allValues.filter(v => !present.has(v)));
    }
    return result;
  });

  slaTimeOptions = signal<OptionItem[]>([
    { id: '10', name: '10%' },
    { id: '20', name: '20%' },
    { id: '30', name: '30%' },
    { id: '40', name: '40%' },
    { id: '50', name: '50%' },
    { id: '60', name: '60%' },
    { id: '70', name: '70%' },
    { id: '80', name: '80%' },
    { id: '90', name: '90%' },
  ]);

  ngOnInit(): void {
    this.notificationForm = this.fb.group({});
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    forkJoin({
      types: this.accountSettingsService.getNotificationTypes(),
      settings: this.accountSettingsService.getNotificationSettings(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ types, settings }) => {
          const fetchedTypes = types.data || [];
          this.notificationTypes.set(fetchedTypes);
          this.SettingsForm(fetchedTypes);
          this.notificationSettings.set(settings.data);
          this.mapSettingsToForm();
          this.setupFormSubscription();

          this.isLoading.set(false);
          this.isInitialized.set(true);
        },
        error: () => {
          this.isLoading.set(false);
          this.isInitialized.set(true);
        },
      });
  }

  private channelKey(typeValue: string, channelValue: string): string {
    return `${typeValue}_${channelValue}`;
  }

  private prefKey(typeValue: string): string {
    return `${typeValue}_preferences`;
  }

  private SettingsForm(types: any[]): void {
    const group: any = {};
    for (const type of types) {
      // Always create controls for every channel; default missing ones to false
      for (const channel of this.allChannels) {
        const exists = type.channels?.some(
          (c: NotificationChannel) => c.value === channel.value
        );
        group[this.channelKey(type.value, channel.value)] = [
          exists ? true : false,
        ];
      }
      if (type.has_preferences) {
        group[this.prefKey(type.value)] = [
          type.default_preferences
            ? type.default_preferences.toString()
            : SlaNotificationTimeSetting.DEFAULT_TIME_SETTING,
        ];
      }
    }
    this.notificationForm = this.fb.group(group);
  }

  private mapSettingsToForm(): void {
    for (const setting of this.notificationSettings()) {
      const typeValue = setting.notification_type.value;

      for (const channelValue of Object.values(NotificationChannelValue)) {
        const key = this.channelKey(typeValue, channelValue);
        if (this.notificationForm.contains(key)) {
          this.notificationForm
            .get(key)
            ?.setValue(setting.channels.includes(channelValue), {
              emitEvent: false,
            });
        }
      }

      const pKey = this.prefKey(typeValue);
      if (this.notificationForm.contains(pKey)) {
        const typeConfig = this.notificationTypes().find(
          t => t.value === typeValue
        );
        const prefValue =
          setting.preferences != null
            ? setting.preferences.toString()
            : typeConfig?.default_preferences != null
              ? typeConfig.default_preferences.toString()
              : null;

        this.notificationForm
          .get(pKey)
          ?.setValue(prefValue, { emitEvent: false });
      }
    }
  }

  private setupFormSubscription(): void {
    this.notificationForm.valueChanges
      .pipe(
        debounceTime(500),
        startWith(this.notificationForm.value),
        pairwise(),
        takeUntil(this.destroy$)
      )
      .subscribe(
        ([prev, curr]: [Record<string, unknown>, Record<string, unknown>]) => {
          if (this.isInitialized()) {
            this.detectAndSaveChangedSetting(prev, curr);
          }
        }
      );
  }

  private detectAndSaveChangedSetting(
    prev: Record<string, unknown>,
    curr: Record<string, unknown>
  ): void {
    for (const type of this.notificationTypes()) {
      const pKey = this.prefKey(type.value);

      const channelChanged = type.channels.some(
        channel =>
          prev[this.channelKey(type.value, channel.value)] !==
          curr[this.channelKey(type.value, channel.value)]
      );
      const prefChanged = type.has_preferences && prev[pKey] !== curr[pKey];

      if (!channelChanged && !prefChanged) continue;

      const allOff = type.channels.every(
        (channel: NotificationChannel) =>
          !curr[this.channelKey(type.value, channel.value)]
      );
      if (allOff && type.has_preferences && type.default_preferences != null) {
        const defaultVal = type.default_preferences.toString();
        this.notificationForm
          .get(pKey)
          ?.setValue(defaultVal, { emitEvent: false });
        curr[pKey] = defaultVal;
      }

      this.saveSettingByType(type.value, curr, type);
    }
  }

  private saveSettingByType(
    typeValue: string,
    formValue: Record<string, unknown>,
    typeConfig: NotificationType
  ): void {
    const channels = typeConfig.channels
      .filter(channel => formValue[this.channelKey(typeValue, channel.value)])
      .map(channel => channel.value);

    const payload: { channels: string[]; preferences?: number } = { channels };
    if (typeConfig.has_preferences) {
      payload.preferences =
        parseInt(formValue[this.prefKey(typeValue)] as string) ||
        typeConfig.default_preferences ||
        undefined;
    }

    this.accountSettingsService
      .saveNotificationSettings(typeValue, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  hasAnySlaChannelEnabled(typeValue: string): boolean {
    const typeConfig = this.notificationTypes().find(
      t => t.value === typeValue
    );
    if (!typeConfig) return false;
    return typeConfig.channels.some(
      channel =>
        this.notificationForm.get(this.channelKey(typeValue, channel.value))
          ?.value
    );
  }
}
